import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Paper, Grid, Divider, CircularProgress,
    Alert, Chip, Button, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Stack
} from '@mui/material';
import {
    KeyboardBackspace, ReceiptLong, PersonOutlined,
    LocationOnOutlined, PaymentOutlined, PhoneOutlined,
    EmailOutlined, PrintOutlined
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { getFoodBookingById } from '../../services/Api';

const ViewBookingDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [booking, setBooking] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchDetails = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await getFoodBookingById(id);
                const data = response?.data || response?.booking || response || {};
                setBooking(data);
            } catch (err) {
                setError(err.response?.data?.message || 'Failed to load booking details');
            } finally {
                setLoading(false);
            }
        };

        if (id) fetchDetails();
    }, [id]);

    const getStatusColor = (status) => {
        const s = status?.toLowerCase();
        if (s === 'delivered' || s === 'completed' || s === 'success') return 'success';
        if (s === 'cancelled' || s === 'rejected' || s === 'failed') return 'error';
        if (s === 'confirmed' || s === 'ready_for_pickup') return 'primary';
        return 'warning';
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                <CircularProgress sx={{ color: 'primary.main' }} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                <Button startIcon={<KeyboardBackspace />} onClick={() => navigate(-1)}>
                    Back to Bookings
                </Button>
            </Box>
        );
    }

    if (!booking) return null;
    const items = booking.items || [];

    return (
        <React.Fragment>
            <style>
                {`
            @media print {
                html, body { height: auto !important; overflow: visible !important; min-width: 100% !important; }
                @page { size: auto; margin: 0mm; }
                body { margin: 1cm; padding: 0; }
                .print-hidden { display: none !important; }
                .print-full-width { display: block !important; width: 100% !important; max-width: 100% !important; flex: 0 0 100% !important; margin: 0 !important; padding: 0 !important; }
                .print-visible-overflow { overflow: visible !important; overflow-x: visible !important; white-space: normal !important; box-shadow: none !important; }
                .print-table { width: 100% !important; min-width: 100% !important; table-layout: fixed !important; }
            }
        `}
            </style>
            <Box sx={{ pb: { xs: 6, print: 0 }, px: { xs: 2, md: 4, print: 0 } }}>
                {/* Minimal Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5, flexWrap: { xs: 'wrap', print: 'nowrap' } }}>
                            <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', fontSize: { print: '1.25rem' }, whiteSpace: { print: 'nowrap' } }}>
                                Order #{booking.bookingNumber || booking.id}
                            </Typography>
                            <Chip
                                label={(booking.status || 'Pending')}
                                color={getStatusColor(booking.status)}
                                size="small"
                                sx={{ fontWeight: 600, textTransform: 'capitalize', borderRadius: '8px' }}
                            />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                            {new Date(booking.createdAt || booking.date || Date.now()).toLocaleString()}
                        </Typography>
                    </Box>
                    <Box className="print-hidden" sx={{ display: 'flex', gap: 2 }}>
                        <Button
                            variant="outlined"
                            startIcon={<PrintOutlined />}
                            onClick={() => window.print()}
                            sx={{ borderRadius: '8px', color: 'primary.main', borderColor: 'primary.main', textTransform: 'none' }}
                        >
                            Print Invoice
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<KeyboardBackspace />}
                            onClick={() => navigate(-1)}
                            sx={{ borderRadius: '8px', color: 'text.primary', borderColor: 'divider', textTransform: 'none' }}
                        >
                            Back
                        </Button>
                    </Box>
                </Box>

                <Grid container spacing={{ xs: 3, print: 0 }} className="print-full-width">
                    {/* Left Column: Items & Summary */}
                    <Grid size={{ xs: 12, md: 8 }} className="print-full-width">
                        <Paper className="print-visible-overflow" elevation={0} sx={{ p: 0, borderRadius: '12px', border: '1px solid', borderColor: 'divider', overflow: { xs: 'hidden', print: 'visible' } }}>
                            <Box sx={{ p: { xs: 2.5, print: 1.5 }, borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#f9f9fc' }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Order Summary</Typography>
                            </Box>

                            {items.length > 0 ? (
                                <TableContainer className="print-visible-overflow" sx={{ overflowX: { xs: 'auto', print: 'visible' }, overflow: 'visible' }}>
                                    <Table className="print-table" sx={{ minWidth: { xs: 500, print: '100%' } }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ color: 'text.secondary', fontWeight: 500 }}>Item Description</TableCell>
                                                <TableCell align="center" sx={{ color: 'text.secondary', fontWeight: 500 }}>Qty</TableCell>
                                                <TableCell align="right" sx={{ color: 'text.secondary', fontWeight: 500 }}>Price</TableCell>
                                                <TableCell align="right" sx={{ color: 'text.secondary', fontWeight: 500 }}>Total</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {items.map((item, index) => (
                                                <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                    <TableCell>
                                                        <Typography sx={{ fontWeight: 500 }}>{item.menuItemName || item.name || 'Unknown Item'}</Typography>
                                                        {item.menuItemVariant && (
                                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                                {item.menuItemVariant}
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="center">{item.quantity || 1}</TableCell>
                                                    <TableCell align="right">₹{Number(item.unitPrice || item.price || 0).toFixed(2)}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 500 }}>
                                                        ₹{Number(item.totalPrice || ((item.quantity || 1) * (item.unitPrice || item.price || 0))).toFixed(2)}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                <Box sx={{ p: 4, textAlign: 'center' }}>
                                    <Typography color="text.secondary">No items found.</Typography>
                                </Box>
                            )}

                            <Divider />

                            {/* Totals Section */}
                            <Box sx={{ p: { xs: 3, print: 1.5 }, display: 'flex', justifyContent: 'flex-end', bgcolor: '#fbfbfe' }}>
                                <Box sx={{ width: '100%', maxWidth: 320 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                                        <Typography color="text.secondary">Subtotal</Typography>
                                        <Typography sx={{ fontWeight: 500 }}>₹{Number(booking.itemsTotal || 0).toFixed(2)}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                                        <Typography color="text.secondary">Tax</Typography>
                                        <Typography sx={{ fontWeight: 500 }}>₹{Number(booking.gstAmount || 0).toFixed(2)}</Typography>
                                    </Box>
                                    <Divider sx={{ my: 2 }} />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography sx={{ fontWeight: 600, fontSize: '1.1rem' }}>Grand Total</Typography>
                                        <Typography sx={{ fontWeight: 700, fontSize: '1.2rem', color: 'primary.main' }}>
                                            ₹{Number(booking.grandTotal || 0).toFixed(2)}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Right Column: Information Cards hidden during print */}
                    <Grid size={{ xs: 12, md: 4 }} className="print-hidden">
                        <Stack spacing={3}>

                            {/* Customer */}
                            <Paper elevation={0} sx={{ p: 3, borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <PersonOutlined sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />
                                    <Typography sx={{ fontWeight: 600 }}>Customer</Typography>
                                </Box>
                                <Typography sx={{ fontWeight: 500, mb: 1 }}>
                                    {booking.user?.name || 'Guest User'}
                                </Typography>
                                <Stack spacing={1} sx={{ mt: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <PhoneOutlined sx={{ color: 'text.secondary', mr: 1.5, fontSize: 18 }} />
                                        <Typography variant="body2" color="text.secondary">
                                            {booking.user?.mobile ? `+91 ${booking.user.mobile}` : 'No phone'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <EmailOutlined sx={{ color: 'text.secondary', mr: 1.5, fontSize: 18 }} />
                                        <Typography variant="body2" color="text.secondary">
                                            {booking.user?.email || 'No email'}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Paper>

                            {/* Delivery */}
                            <Paper elevation={0} sx={{ p: 3, borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <LocationOnOutlined sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />
                                    <Typography sx={{ fontWeight: 600 }}>Delivery Address</Typography>
                                </Box>
                                {booking.address ? (
                                    <Box>
                                        <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                                            {booking.address.street}
                                        </Typography>
                                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                                            {booking.address.area}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                            {booking.address.city}, {booking.address.state} {booking.address.pincode}
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        No delivery information.
                                    </Typography>
                                )}
                            </Paper>

                            {/* Payment */}
                            <Paper elevation={0} sx={{ p: 3, borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <PaymentOutlined sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />
                                    <Typography sx={{ fontWeight: 600 }}>Payment</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Payment Method
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500, textTransform: 'capitalize' }}>
                                        Online
                                    </Typography>
                                </Box>
                                <Divider sx={{ my: 1.5 }} />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Status
                                    </Typography>
                                    <Chip
                                        label={booking.paymentStatus || 'Pending'}
                                        size="small"
                                        color={getStatusColor(booking.paymentStatus)}
                                        sx={{ height: 24, fontSize: '0.75rem', textTransform: 'capitalize', borderRadius: '4px' }}
                                    />
                                </Box>
                            </Paper>
                        </Stack>
                    </Grid>

                </Grid>
            </Box>
        </React.Fragment>
    );
};

export default ViewBookingDetails;
